import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Grid, Popover, Typography, Alert } from "@mui/material";
import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { State, Props } from "./interfaces";
import { SuggestionContainer, Suggestion } from "./styles";

import { ExchangeRateDTO } from "../../../../../../../../../models/exchange-rates";

import { ExchangeRatesFilter } from "../../../../../../../../../models/exchange-rates";
import { ExchangeRatesApiService } from "../../../../../../../../../services/exchange-rates/exchange-rates-api.service";

const ExchangeRateSuggestions = ({ date, sourceCurrency, destinationCurrency, legalEntity, handleNotify }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_travel_expense.exchange_rate_suggestion";

  const [state, setState] = useState<State>({
    error: "",
    open: false,
    anchorEl: null,
    exchangeRateSuggestions: [],
    xRateCopiedIdx: null,
  });

  const loadExchangeRates = useCallback(
    async (signal?: AbortSignal) => {
      const apiService = new ExchangeRatesApiService();
      apiService.useLoadItemsPath("/GetSuggestedExchangeRate");

      try {
        const response = await apiService.loadItems(
          {
            sourceId: sourceCurrency?.id,
            destinationId: destinationCurrency?.id,
            year: new Date(date).getFullYear(),
            month: new Date(date).getMonth() + 1,
            legalEntityId: legalEntity?.id,
          } as ExchangeRatesFilter,
          undefined,
          undefined,
          undefined,
          undefined,
          60,
          signal
        );

        if (response.error || signal?.aborted || !signal) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        } else {
          setState((state) => ({
            ...state,
            exchangeRateSuggestions: response.items,
          }));

          !!response.items[0] && handleNotify && handleNotify(response.items[0].rate);
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [date, destinationCurrency, sourceCurrency, legalEntity]
  );

  useEffect(() => {
    const abortController = new AbortController();

    !!legalEntity && !!date && !!destinationCurrency && !!sourceCurrency && loadExchangeRates(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [date, destinationCurrency, sourceCurrency, legalEntity]);

  useEffect(() => {
    !state.open &&
      setState((state) => ({
        ...state,
        xRateCopiedIdx: null,
      }));
  }, [state.open]);

  return (
    <>
      <IconButton
        onClick={(event) => {
          setState((state) => ({
            ...state,
            open: true,
            anchorEl: event.currentTarget,
          }));
        }}
        title={`${t(`${tRP}.title`)}`}
      >
        <FontAwesomeIcon icon={faInfoCircle} />
      </IconButton>
      <Popover
        open={state.open}
        anchorEl={state.anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={() => {
          setState((state) => ({ ...state, open: false }));
        }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            padding: "10px",
            //   backgroundColor: "rgb(245, 245, 245)",
            alignItems: "flex-start",
            width: "500px",
          },
        }}
      >
        <Grid>
          <Typography>
            <b>{`${t(`${tRP}.title`)}`} : </b>
          </Typography>
          {state.exchangeRateSuggestions.length > 0 ? (
            state.exchangeRateSuggestions.map((xRate: ExchangeRateDTO, idx) => (
              <SuggestionContainer
                key={idx}
                onClick={() => {
                  navigator.clipboard.writeText(xRate.rate.toString());
                  setState((state) => ({
                    ...state,
                    xRateCopiedIdx: idx,
                  }));
                }}
              >
                <Suggestion isSelected={state.xRateCopiedIdx === idx}>
                  {`${xRate.rate}`}
                  {/* {`${xRate.rate} (${xRate.dataSource?.name})`} */}
                </Suggestion>
                {state.xRateCopiedIdx === idx && <Alert severity={"info"}>Copied to clipboard</Alert>}
              </SuggestionContainer>
            ))
          ) : (
            <Typography>{`${t(`${tRP}.no_exchange_rate_warning`)}`}</Typography>
          )}
        </Grid>
      </Popover>
    </>
  );
};

export default React.memo(ExchangeRateSuggestions);
